import { useQuery } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import type { EmailModeView } from "@/lib/types";

/**
 * Shared query so every screen reads one cached answer rather than each
 * re-fetching the deployment's mode.
 */
export function useEmailMode() {
  return useQuery({
    queryKey: ["email-mode"],
    queryFn: () => api.get<EmailModeView>("/api/v1/settings/email-mode"),
    // Deployment configuration only changes on a restart, so this is effectively
    // static for a session. Refetching it on every screen would be noise.
    staleTime: 5 * 60_000,
  });
}

/**
 * Standing notice that this deployment delivers nothing.
 *
 * <p>Deliberately always visible rather than dismissible. A sandboxed stack is
 * indistinguishable from a live one once you are inside the CRM — messages
 * appear, statuses advance — and the two mistakes it prevents are asymmetric:
 * believing a rehearsal was real costs a wrong conclusion, believing production
 * is a rehearsal costs real people receiving real mail. The banner stays.
 */
export default function EmailModeBanner() {
  const { data } = useEmailMode();
  if (!data?.sandbox) return null;

  return (
    <div
      role="status"
      className="flex items-start gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
    >
      <FlaskConical className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
      <div>
        <span className="font-semibold">Sandbox mode — no email is delivered.</span>{" "}
        Messages are composed, recorded and marked sent, but nothing leaves this server.
        {data.inboundPollingEnabled ? (
          <>
            {" "}
            Inbound polling is <span className="font-medium">enabled</span> on this
            deployment: a strategy whose sandbox mode is{" "}
            <code>test_address</code> still connects to its mail server and reads real
            replies. Strategies left on <code>isolated</code> contact nothing.
          </>
        ) : null}
        {!data.deliveryEvents ? (
          <>
            {" "}
            Delivery events are not reported by this provider, so messages stop at{" "}
            <span className="font-medium">Sent</span> and open/reply rates stay at zero
            unless simulated.
          </>
        ) : null}
      </div>
    </div>
  );
}
